import { useEffect, useRef, useState } from "react";
import type { StudioApiClient } from "../../../api.js";
import type { V10ScanRow } from "../adapters/v10-fixtures.js";
import { V10BatchAuditPanel } from "../components/V10BatchAuditPanel.js";
import { V10FixKitPanel } from "../components/V10FixKitPanel.js";
import { V10RelaunchPanel } from "../components/V10RelaunchPanel.js";
import { V10Btn, V10Kicker } from "../components/V10Primitives.js";
import type { V10IntakeFocus } from "../routing/v10-routing.js";

export function V10ScansProofView({
  intake,
  scanRows,
  loading,
  error,
  busy,
  api,
  onRunScan,
  onOpenRelaunchSheet,
  onAttachExternalProof,
}: {
  readonly intake: V10IntakeFocus | null;
  readonly scanRows: readonly V10ScanRow[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly busy: boolean;
  readonly api: StudioApiClient;
  readonly onRunScan: (url: string) => void;
  readonly onOpenRelaunchSheet: (scanUrl?: string) => void;
  readonly onAttachExternalProof: (url: string) => void;
}) {
  const relaunchRef = useRef<HTMLInputElement>(null);
  const externalRef = useRef<HTMLInputElement>(null);
  const [scanUrl, setScanUrl] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);

  useEffect(() => {
    if (intake === "relaunch") relaunchRef.current?.focus();
    if (intake === "external-proof") externalRef.current?.focus();
  }, [intake]);

  useEffect(() => {
    if (scanRows.length === 0) {
      setSelectedClaimId(null);
      return;
    }
    if (!selectedClaimId || !scanRows.some((row) => row.id === selectedClaimId)) {
      setSelectedClaimId(scanRows[0]!.id);
    }
  }, [scanRows, selectedClaimId]);

  return (
    <section className="v10-screen" aria-label="Scans and Proof">
      <div className="v10-section-head v10-section-head--flush">
        <div>
          <V10Kicker>Scans / Proof</V10Kicker>
          <h2>Relaunch scans and external proof intake.</h2>
          <p className="v10-muted">Contextual relaunch revenue — not a generic SEO dashboard.</p>
        </div>
      </div>

      <div className="v10-grid-two">
        <article className="v10-card v10-card--major">
          <h3>Existing client site URL</h3>
          <label htmlFor="v10-scan-url">Website URL</label>
          <input
            id="v10-scan-url"
            ref={relaunchRef}
            type="url"
            value={scanUrl}
            onChange={(e) => setScanUrl(e.target.value)}
            placeholder="https://example.com"
            disabled={busy}
          />
          <div className="v10-hero-actions">
            <V10Btn variant="primary" disabled={busy} onClick={() => onRunScan(scanUrl)}>
              {busy ? "Scanning…" : "Start scan"}
            </V10Btn>
            <V10Btn disabled={busy} onClick={() => onOpenRelaunchSheet(scanUrl)}>
              Start relaunch project
            </V10Btn>
          </div>
        </article>

        <article className="v10-card v10-card--major">
          <h3>External proof URL</h3>
          <label htmlFor="v10-external-url">External site URL</label>
          <input
            id="v10-external-url"
            ref={externalRef}
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://example.com"
            disabled={busy}
          />
          <div className="v10-hero-actions">
            <V10Btn
              variant="primary"
              disabled={busy || !externalUrl.trim()}
              onClick={() => {
                setScanMessage("Opening New Work for external proof project.");
                onAttachExternalProof(externalUrl);
              }}
            >
              Attach proof
            </V10Btn>
          </div>
        </article>
      </div>

      {scanMessage ? (
        <p className="v10-action-status" role="status">
          {scanMessage}
        </p>
      ) : null}

      {error ? (
        <p className="v10-validation" role="alert">
          {error}
        </p>
      ) : null}

      <section className="v10-queue-section" aria-label="Recent scans">
        <h2>Recent scans</h2>
        {loading ? <p className="v10-muted">Loading scan opportunities…</p> : null}
        {!loading && scanRows.length === 0 ? <p className="v10-muted">No claimed scans yet.</p> : null}
        <ul className="v10-inbox-list">
          {scanRows.map((scan) => (
            <li key={scan.id} className="v10-inbox-row">
              <div>
                <strong>{scan.label}</strong>
                <p className="v10-muted">{scan.url}</p>
                <p>
                  {scan.status} · <span className="v10-age">{scan.ageLabel}</span>
                </p>
              </div>
              <div className="v10-inline-actions">
                <V10Btn
                  variant={selectedClaimId === scan.id ? "gold" : "default"}
                  onClick={() => setSelectedClaimId(scan.id)}
                >
                  Select
                </V10Btn>
                <V10Btn variant="primary" onClick={() => onOpenRelaunchSheet(scan.url)}>
                  Start relaunch
                </V10Btn>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {selectedClaimId ? (
        <section className="v10-grid-two" aria-label="Opportunity tools">
          <V10FixKitPanel api={api} claimId={selectedClaimId} />
          <V10RelaunchPanel api={api} claimId={selectedClaimId} />
        </section>
      ) : null}

      <V10BatchAuditPanel api={api} />
    </section>
  );
}
